import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";

// Single extension point: every schema in this package must import `z` from
// here (never from "zod" directly) so `.openapi()` metadata is always available
// and the whole package shares one zod instance.
extendZodWithOpenApi(z);

/**
 * Zod 3's `datetime()` grammar, which every client of /v1 was written against
 * (D-643). Zod 4 requires the seconds whenever a time carries `Z` or an
 * offset, and an offset's colon, so it refuses `2026-12-16T00:00Z`, which the
 * calendar contract states it admits (spec 03 §3.3b), and `+0500`. This is
 * zod 3's `datetimeRegex`, digit for digit.
 */
export function zod3DatetimeRegex(args: {
  readonly precision?: number | null;
  readonly offset?: boolean;
  readonly local?: boolean;
}): RegExp {
  const date =
    "((\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-((0[13578]|1[02])-(0[1-9]|[12]\\d|3[01])|(0[469]|11)-(0[1-9]|[12]\\d|30)|(02)-(0[1-9]|1\\d|2[0-8])))";
  let seconds = "[0-5]\\d";
  if (args.precision) seconds = `${seconds}\\.\\d{${args.precision}}`;
  else if (args.precision == null) seconds = `${seconds}(\\.\\d+)?`;
  const secondsQuantifier = args.precision ? "+" : "?";
  const time = `([01]\\d|2[0-3]):[0-5]\\d(:${seconds})${secondsQuantifier}`;
  const zones = [args.local ? "Z?" : "Z"];
  if (args.offset) zones.push("([+-]\\d{2}:?\\d{2})");
  return new RegExp(`^${date}T${time}(${zones.join("|")})$`);
}

type DatetimeParams = Exclude<Parameters<typeof z.iso.datetime>[0], string | undefined>;

/**
 * An ISO 8601 instant in zod 3's grammar (D-643). It is zod's own ISO datetime
 * check, carrying the `datetime` format, so the OpenAPI document, the agent
 * JSON Schema and an issue's code are what they are for any datetime; only the
 * pattern it tests is zod 3's. Every instant in the contracts is read through
 * it, in place of zod 4's `z.iso.datetime()`, and `test/zod-wire.test.ts`
 * holds the grammar case by case.
 */
export function isoDateTime(params?: DatetimeParams): z.ZodISODateTime {
  const pattern = zod3DatetimeRegex({
    precision: params?.precision ?? null,
    offset: params?.offset ?? false,
    local: params?.local ?? false,
  });
  // The ISO datetime schema keeps a `pattern` it is given ($ZodISODateTime),
  // though its parameter type leaves the key out.
  return z.iso.datetime({ ...params, pattern } as DatetimeParams);
}

export { z };
